import { useState } from 'react';
import {
  ALL,
  CATEGORIES,
  PRAYER_STATUS,
  PRIORITY,
  SORT_KEYS,
  nextSort,
  priorityLabel,
  sortLabel,
  statusLabel,
} from '../../domain.ts';
import type { Filter, Sort } from '../../domain.ts';

/**
 * C1 의 두 줄 — 정렬과 조건.
 *
 * 생김새가 같은 버튼이 나란히 있으면 무엇이 차례를 바꾸고 무엇이 범위를
 * 줄이는지 알 수 없어, 줄 앞에 이름을 붙여 못박았습니다.
 *
 *   정렬      다시 누르면 ↑ ↓ 가 뒤집힙니다. 화살표는 켜진 것에만 붙습니다
 *   조건      누르면 그 버튼 바로 아래에서 고르는 줄이 펼쳐집니다 (C1-1 ~ C1-3)
 *
 * 두 줄은 화면을 넘으면 좌우로 밀어서 봅니다. 줄바꿈을 하지 않는 이유는,
 * 버튼이 늘어날 때마다 목록이 아래로 밀려 내려가기 때문입니다.
 * 반대로 펼쳐지는 줄은 줄바꿈을 합니다 — 고르는 중에는 선택지가 한눈에
 * 다 보이는 편이 낫습니다.
 */

/** 펼쳐 고르는 셋. 대표는 켜기/끄기라 펼칠 것이 없습니다 */
type Open = 'category' | 'status' | 'priority' | null;

interface Props {
  sort: Sort;
  onSort: (sort: Sort) => void;
  filter: Filter;
  onFilter: (filter: Filter) => void;
}

export function FilterBar({ sort, onSort, filter, onFilter }: Props) {
  const [open, setOpen] = useState<Open>(null);

  /** 같은 것을 다시 누르면 고르지 않고 접힙니다 */
  const toggle = (which: Exclude<Open, null>) => setOpen((o) => (o === which ? null : which));

  /** 하나를 누르면 바로 접히고 목록이 걸러집니다 — 확인 버튼이 없습니다 */
  const pick = (changes: Partial<Filter>) => {
    onFilter({ ...filter, ...changes });
    setOpen(null);
  };

  return (
    <>
      <div className="frow">
        <span className="frow__key">정렬</span>
        <div className="seg seg--scroll" role="group" aria-label="정렬">
          {SORT_KEYS.map((key) => {
            const on = key === sort.key;
            return (
              <button
                key={key}
                type="button"
                className={on ? 'sg sg--on' : 'sg'}
                aria-pressed={on}
                onClick={() => onSort(nextSort(sort, key))}
              >
                {sortLabel[key]}
                {on && (sort.dir === 'down' ? ' ↓' : ' ↑')}
              </button>
            );
          })}
        </div>
      </div>

      <div className="frow">
        <span className="frow__key">조건</span>
        <div className="seg seg--scroll" role="group" aria-label="조건">
          {/* 켜진 색은 «펼쳐져 있다» 와 «걸려 있다» 둘 다입니다. 어느 쪽이든
              지금 이 버튼이 목록에 관여하고 있다는 뜻이라 같은 색으로 둡니다 */}
          <Tab
            label="영역"
            on={open === 'category' || filter.category !== ALL}
            expanded={open === 'category'}
            onClick={() => toggle('category')}
          />
          <Tab
            label="기도 상태"
            on={open === 'status' || filter.status !== ALL}
            expanded={open === 'status'}
            onClick={() => toggle('status')}
          />
          <Tab
            label="중요도"
            on={open === 'priority' || filter.priority !== ALL}
            expanded={open === 'priority'}
            onClick={() => toggle('priority')}
          />
          <Tab
            label="대표"
            on={filter.representativeOnly}
            onClick={() => {
              setOpen(null);
              onFilter({ ...filter, representativeOnly: !filter.representativeOnly });
            }}
          />
        </div>
      </div>

      {open === 'category' && (
        <Picker
          label="영역"
          options={CATEGORIES.map((c) => ({ value: c, label: c }))}
          value={filter.category}
          onPick={(category) => pick({ category })}
        />
      )}
      {open === 'status' && (
        <Picker
          label="기도 상태"
          options={PRAYER_STATUS.map((s) => ({ value: s, label: statusLabel[s] }))}
          value={filter.status}
          onPick={(status) => pick({ status })}
        />
      )}
      {open === 'priority' && (
        <Picker
          label="중요도"
          options={PRIORITY.map((p) => ({ value: p, label: priorityLabel[p] }))}
          value={filter.priority}
          crit="URGENT"
          onPick={(priority) => pick({ priority })}
        />
      )}
    </>
  );
}

function Tab({
  label,
  on,
  expanded,
  onClick,
}: {
  label: string;
  on: boolean;
  expanded?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={on ? 'sg sg--on' : 'sg'}
      aria-pressed={on}
      {...(expanded === undefined ? {} : { 'aria-expanded': expanded })}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

/**
 * 버튼 바로 아래에서 펼쳐지는 줄.
 *
 * 화면을 새로 열지 않으므로 목록이 그대로 남고, 무엇을 고르는 중인지 배경이
 * 계속 보입니다. 고르는 모양은 C2 · C4 의 영역 필드와 같습니다 —
 * 같은 일에 같은 생김새를 써서 한 번 익히면 여러 곳에서 통합니다.
 */
function Picker<T extends string>({
  label,
  options,
  value,
  crit,
  onPick,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T | typeof ALL;
  /** 켜졌을 때만 빨강으로 칠할 값 — 중요도 '긴급' */
  crit?: T;
  onPick: (value: T | typeof ALL) => void;
}) {
  const chip = (v: T | typeof ALL, text: string) => {
    const on = v === value;
    return (
      <button
        key={v}
        type="button"
        className={on ? (v === crit ? 'sg sg--crit' : 'sg sg--on') : 'sg'}
        aria-pressed={on}
        onClick={() => onPick(v)}
      >
        {text}
      </button>
    );
  };

  return (
    <div className="picker">
      <div className="seg" role="group" aria-label={`${label} 고르기`}>
        {/* 전체 = 이 조건을 쓰지 않는 상태 */}
        {chip(ALL, '전체')}
        {options.map((o) => chip(o.value, o.label))}
      </div>
    </div>
  );
}
